import dayjs from 'dayjs';

import { withStoreCreateBody, getStoreActorId } from '../hooks/useStoreScope';

import { parseWholeQty, parsePositiveRate, computeLineDiscountAmount } from '../utils';

import { meaningfulItemName, enrichLineWithItemTracking } from './itemDisplay';

import { GRN_PRICING_OVERRIDE_ROLES } from '../constants/grnManagement';

import { normalizeInvRole, INV_ROLES } from './inventoryRoles';



function omitEmptyStrings(obj) {

  const out = { ...obj };

  Object.keys(out).forEach((key) => {

    if (out[key] === '') delete out[key];

  });

  return out;

}



function formatDecimal(value, fallback = '0') {

  if (value === undefined || value === null || value === '') return fallback;

  const n = Number(value);

  return Number.isFinite(n) ? n.toFixed(2) : fallback;

}



export function todayGrnDate() {

  return dayjs().format('YYYY-MM-DD');

}



export function canOverrideGrnPricing() {

  const raw = sessionStorage.getItem('roleType');

  // Store Manager and the admin roles are matched through the shared normalizer so
  // the spelling the backend uses (store_manager / Store Manager) doesn't matter,
  // and so `super admin` is covered without editing the legacy list below. Stays a
  // plain function, not a hook — this is also called from line validation below.
  const invRole = normalizeInvRole(raw);
  if (invRole === INV_ROLES.STORE_MANAGER || invRole === INV_ROLES.ADMIN) return true;

  const role = String(raw || '').toLowerCase().trim();

  return GRN_PRICING_OVERRIDE_ROLES.some(r => role === r.toLowerCase());

}



export function isGrnLineStarted(line, isDirect = false) {

  if (isDirect) return Boolean(line.item_id);

  return Boolean(line.po_item_id || line.purchase_order_item_id);

}



export function getActiveGrnLines(lines = [], isDirect = false) {

  return lines.filter(l => isGrnLineStarted(l, isDirect));

}



export function minGrnExpiryDate(mfgDate, today = todayGrnDate()) {

  const base = mfgDate && mfgDate > today ? mfgDate : today;

  return dayjs(base).add(1, 'day').format('YYYY-MM-DD');

}



function ratesDiffer(a, b) {

  if (a === '' || a == null || b === '' || b == null) return false;

  return Math.abs(Number(a) - Number(b)) > 0.001;

}



export function isPoRateOverridden(line) {

  return line.po_unit_rate !== '' && line.po_unit_rate != null

    && parsePositiveRate(line.unit_rate)

    && ratesDiffer(line.unit_rate, line.po_unit_rate);

}



function validateLineFreeQty(line, errors) {

  const raw = line.free_quantity;

  if (raw === '' || raw == null) return;

  const n = Number(raw);

  if (!Number.isFinite(n) || n < 0) {

    errors.free_quantity = 'Must be zero or greater';

  }

}



function validateLineDates(line, errors) {

  const today = todayGrnDate();

  if (line.manufacturing_date && line.manufacturing_date > today) {

    errors.manufacturing_date = 'Manufacturing date cannot be in the future';

  }

  if (line.expiry_date) {

    if (line.expiry_date <= today && !line.allow_expired_override) {

      errors.expiry_date = 'Expiry date must be after today (enable override to accept expired stock)';

    } else if (line.manufacturing_date && line.expiry_date <= line.manufacturing_date) {

      errors.expiry_date = 'Expiry date must be after manufacturing date';

    }

  }

}



function parsePositiveAmount(value) {

  const n = Number(value);

  return Number.isFinite(n) && n > 0 ? n : null;

}



/** Sale rate entered by user, or MRP when sale rate is left blank. */

function resolveGrnLineSaleRate(line) {

  const sale = parsePositiveAmount(line.sale_rate);

  if (sale != null) return sale;

  return parsePositiveAmount(line.mrp);

}



function formatGrnLinePricing(line) {

  const mrp = formatDecimal(line.mrp);

  const saleRate = line.sale_rate ? formatDecimal(line.sale_rate) : mrp;

  return { mrp, sale_rate: saleRate };

}



function validateLinePricing(line, errors) {

  const rate = parsePositiveRate(line.unit_rate);

  const mrp = parsePositiveAmount(line.mrp);

  if (!mrp) {

    errors.mrp = 'MRP is required';

    return;

  }

  if (rate && mrp < rate) {

    errors.mrp = 'MRP must be greater than or equal to purchase rate';

  }

  const saleRaw = line.sale_rate;

  const hasExplicitSale = saleRaw !== '' && saleRaw != null;

  if (hasExplicitSale && parsePositiveAmount(saleRaw) == null) {

    errors.sale_rate = 'Sale rate must be greater than zero';

    return;

  }

  const effectiveSale = resolveGrnLineSaleRate(line);

  if (rate && effectiveSale != null && effectiveSale < rate) {

    errors.sale_rate = 'Sale rate cannot be less than purchase rate';

  }

  if (effectiveSale != null && effectiveSale > mrp) {

    errors.sale_rate = 'Sale rate cannot exceed MRP';

  }

}



function validatePoPricingOverrides(line, errors) {

  if (line.po_unit_rate !== '' && line.po_unit_rate != null && parsePositiveRate(line.unit_rate)) {

    if (ratesDiffer(line.unit_rate, line.po_unit_rate) && !line.rate_override_reason?.trim()) {

      errors.rate_override_reason = 'Reason required when changing rate from PO';

    }

  }

  if (!canOverrideGrnPricing()) {

    if (line.po_tax_percent !== '' && line.po_tax_percent != null

      && String(line.tax_percent) !== String(line.po_tax_percent)) {

      errors.tax_percent = 'No permission to override PO tax rate';

    }

    if (line.po_discount_percent !== '' && line.po_discount_percent != null

      && String(line.discount_percent) !== String(line.po_discount_percent)) {

      errors.discount_percent = 'No permission to override PO discount';

    }

  }

}



const LINE_ERROR_FIELD_ORDER = [

  'item_id', 'uom_id', 'received_quantity', 'free_quantity', 'unit_rate',

  'rate_override_reason', 'batch_number', 'manufacturing_date', 'expiry_date',

  'mrp', 'sale_rate', 'tax_percent', 'discount_percent',

];



export const DIRECT_GRN_LINE_DEFAULT = {

  item_id: '',

  item_name: '',

  uom_id: '',

  received_quantity: '',

  batch_number: '',

  manufacturer_batch_number: '',

  manufacturing_date: '',

  expiry_date: '',

  unit_rate: '',

  mrp: '',

  sale_rate: '',

  tax_percent: '0',

  discount_percent: '0',

  discount_amount: '0',

  free_quantity: '0',

  uom_name: '',

  uom_code: '',

  accept_quantity: '',

  reject_quantity: 0,

  rejection_reason: '',

  allow_expired_override: false,

  is_batch_tracked: false,

  is_expiry_tracked: false,

  item_code: '',

};



export const PO_GRN_LINE_DEFAULT = {

  item_id: '',

  item_name: '',

  uom_id: '',

  po_item_id: '',

  purchase_order_item_id: '',

  ordered_quantity: '',

  pending_quantity: '',

  received_quantity: '',

  accept_quantity: '',

  reject_quantity: 0,

  rejection_reason: '',

  allow_expired_override: false,

  batch_number: '',

  expiry_date: '',

  manufacturer_batch_number: '',

  manufacturing_date: '',

  unit_rate: '',

  mrp: '',

  sale_rate: '',

  tax_percent: '0',

  discount_percent: '0',

  discount_amount: '0',

  free_quantity: '0',

  uom_name: '',

  uom_code: '',

  is_batch_tracked: false,

  is_expiry_tracked: false,

  item_code: '',

  po_unit_rate: '',

  po_tax_percent: '',

  po_discount_percent: '',

  rate_override_reason: '',

};



/**
 * Returns first validation failure as { field, message } for UI focus + inline errors.
 * Returns null when valid.
 */
export function validateGrnDetails(grnData, { requireVendor = false } = {}) {

  if (requireVendor && !grnData.vendor_id) {

    return { field: 'vendor_id', message: 'Select a vendor' };

  }

  if (!grnData.receiving_store_id) {

    return { field: 'receiving_store_id', message: 'Select a store' };

  }

  if (!grnData.invoice_number?.trim()) {

    return { field: 'invoice_number', message: 'Enter the invoice number' };

  }

  if (!grnData.invoice_date?.trim()) {

    return { field: 'invoice_date', message: 'Enter the invoice date' };

  }

  if (!grnData.grn_date?.trim()) {

    return { field: 'grn_date', message: 'Enter the GRN date' };

  }

  if (grnData.delivery_date && grnData.delivery_date > todayGrnDate()) {

    return { field: 'delivery_date', message: 'Delivery date cannot be in the future' };

  }

  return null;

}



export function validateDirectGrnDetails(grnData) {

  return validateGrnDetails(grnData, { requireVendor: true });

}



export function getDirectLineValidationErrors(line) {

  const errors = {};

  if (!line.item_id) {

    errors.item_id = 'Select an item';

  }

  if (!line.uom_id) {

    errors.uom_id = 'UOM is required';

  }

  if (!parseWholeQty(line.received_quantity)) {

    errors.received_quantity = 'Must be a positive whole number';

  }

  if (!parsePositiveRate(line.unit_rate)) {

    errors.unit_rate = 'Must be greater than zero';

  }

  if (line.is_batch_tracked && !line.batch_number?.trim()) {

    errors.batch_number = 'Batch number is required for batch-tracked items';

  }

  if (line.is_expiry_tracked && !line.expiry_date?.trim()) {

    errors.expiry_date = 'Expiry date is required for expiry-tracked items';

  }

  validateLineFreeQty(line, errors);

  validateLineDates(line, errors);

  if (line.item_id) {

    validateLinePricing(line, errors);

  }

  return errors;

}



function firstLineValidationError(lines, getErrors) {

  for (const line of lines) {

    const errors = getErrors(line);

    const label = meaningfulItemName(line.item_name, line.item_id) || 'Line item';

    const fields = LINE_ERROR_FIELD_ORDER;

    for (const field of fields) {

      if (errors[field]) return `${label}: ${errors[field]}`;

    }

  }

  return null;

}



function isLineValidationComplete(errors) {

  return !Object.values(errors).some(Boolean);

}



function validateActiveGrnLines(lines, isDirect, getErrors) {

  const active = getActiveGrnLines(lines, isDirect);

  if (!active.length) {

    return 'Add at least one line item';

  }

  if (!active.some(l => parseWholeQty(l.received_quantity))) {

    return 'At least one line must have a received quantity greater than zero';

  }

  return firstLineValidationError(active, getErrors);

}



/** First invalid direct line message for toast, or null. */

export function validateDirectGrnLines(lines = []) {

  return validateActiveGrnLines(lines, true, getDirectLineValidationErrors);

}



export function isDirectLineComplete(line) {

  return isLineValidationComplete(getDirectLineValidationErrors(line));

}



export function getPoLineValidationErrors(line) {

  const errors = getDirectLineValidationErrors(line);

  if (!line.po_item_id && !line.purchase_order_item_id) {

    errors.item_id = 'Select a PO item';

  }

  const pending = Math.floor(Number(line.pending_quantity) || 0);

  const received = Math.floor(Number(line.received_quantity) || 0);

  if (!errors.received_quantity && line.pending_quantity !== '' && line.pending_quantity != null && received > pending) {

    errors.received_quantity = `Cannot exceed pending quantity of ${pending}`;

  }

  validatePoPricingOverrides(line, errors);

  return errors;

}



export function validatePoGrnLines(lines = []) {

  return validateActiveGrnLines(lines, false, getPoLineValidationErrors);

}



export function isPoLineComplete(line) {

  return isLineValidationComplete(getPoLineValidationErrors(line));

}



export function isGrnWizardLinesValid(lines = [], isDirect = false) {

  const active = getActiveGrnLines(lines, isDirect);

  const isComplete = isDirect ? isDirectLineComplete : isPoLineComplete;

  if (!active.length) return false;

  if (!active.some(l => parseWholeQty(l.received_quantity))) return false;

  return active.every(isComplete);

}



export function filterGrnLinesForSubmit(lines = [], isDirect = false) {

  return getActiveGrnLines(lines, isDirect).filter(l => parseWholeQty(l.received_quantity));

}



export function toGrnCreatePayload(grnData, { purchaseOrderId, selectedPo }) {

  return toPoGrnPayload(grnData, [], { purchaseOrderId, selectedPo });

}



/** Atomic PO GRN create — header + items[] for POST /inventory/grns/ */

export function toPoGrnPayload(grnData, lines = [], { purchaseOrderId, selectedPo } = {}) {

  const actorId = getStoreActorId();

  const submitLines = filterGrnLinesForSubmit(lines, false);

  const items = submitLines.map((line) => toPoGrnLineItem(line, actorId));

  const header = omitEmptyStrings({

    grn_number: grnData.grn_number,

    grn_kind: grnData.grn_kind || 'STANDARD',

    purchase_order_id: purchaseOrderId,

    vendor_id: grnData.vendor_id || selectedPo?.vendor_id,

    receiving_store_id: grnData.receiving_store_id,

    tenant_id: selectedPo?.tenant_id,

    hospital_id: selectedPo?.hospital_id,

    invoice_number: grnData.invoice_number?.trim(),

    invoice_date: grnData.invoice_date,

    delivery_challan_number: grnData.delivery_challan_number?.trim(),

    delivery_date: grnData.delivery_date,

    grn_date: grnData.grn_date || todayGrnDate(),

    received_by: actorId || undefined,

    remarks: grnData.remarks?.trim(),

    payment_type: grnData.payment_type || 'CREDIT',

    gate_entry_number: grnData.gate_entry_number?.trim(),

    gate_entry_date: grnData.gate_entry_date,

    place_of_supply: grnData.place_of_supply?.trim(),

    external_po_reference: grnData.external_po_reference?.trim(),

  });



  return withStoreCreateBody({

    ...header,

    items,

  });

}



function toPoGrnLineItem(line, actorId) {

  const ordered = Math.floor(Number(line.ordered_quantity) || 0);

  const received = parseInt(line.received_quantity, 10) || 0;

  return omitEmptyStrings({

    purchase_order_item_id: line.po_item_id || line.purchase_order_item_id,

    item_id: line.item_id,

    uom_id: line.uom_id,

    ordered_quantity: String(ordered),

    received_quantity: String(received),

    batch_number: line.batch_number?.trim(),

    manufacturer_batch_number: line.manufacturer_batch_number?.trim(),

    manufacturing_date: line.manufacturing_date,

    expiry_date: line.expiry_date,

    ...formatGrnLinePricing(line),

    purchase_rate: formatDecimal(line.unit_rate),

    tax_percent: String(Number(line.tax_percent) || 0),

    free_quantity: String(Math.floor(Number(line.free_quantity) || 0)),

    pending_quantity: String(Math.max(0, ordered - received)),

    discount_percent: String(Number(line.discount_percent) || 0),

    discount_amount: formatDecimal(
      computeLineDiscountAmount(received, line.unit_rate, line.discount_percent),
    ),

    rate_override_reason: line.rate_override_reason?.trim(),

    created_by: actorId || undefined,

  });

}



/** Ensure wizard lines are present before atomic GRN create. */

export function validateGrnLinesForCreate(lines = [], { isDirect = false } = {}) {

  return validateActiveGrnLines(
    lines,
    isDirect,
    isDirect ? getDirectLineValidationErrors : getPoLineValidationErrors,
  );

}



export function toDirectGrnPayload(grnData, lines = [], orgScope = {}) {

  const actorId = getStoreActorId();

  const submitLines = filterGrnLinesForSubmit(lines, true);

  const tenant_id = grnData.tenant_id || orgScope.tenant_id;

  const hospital_id = grnData.hospital_id || orgScope.hospital_id;

  const items = submitLines.map((line) => {

    const received = parseInt(line.received_quantity, 10) || 0;

    return omitEmptyStrings({

    item_id: line.item_id,

    uom_id: line.uom_id,

    received_quantity: String(received),

    batch_number: line.batch_number?.trim(),

    manufacturer_batch_number: line.manufacturer_batch_number?.trim(),

    manufacturing_date: line.manufacturing_date,

    expiry_date: line.expiry_date,

    ...formatGrnLinePricing(line),

    purchase_rate: formatDecimal(line.unit_rate),

    tax_percent: String(Number(line.tax_percent) || 0),

    ordered_quantity: '0',

    free_quantity: String(Math.floor(Number(line.free_quantity) || 0)),

    discount_percent: String(Number(line.discount_percent) || 0),

    discount_amount: formatDecimal(
      computeLineDiscountAmount(
        parseInt(line.received_quantity, 10) || 0,
        line.unit_rate,
        line.discount_percent,
      ),
    ),

    });

  });



  const header = omitEmptyStrings({

    tenant_id,

    hospital_id,

    grn_number: grnData.grn_number,

    vendor_id: grnData.vendor_id,

    receiving_store_id: grnData.receiving_store_id,

    invoice_number: grnData.invoice_number?.trim(),

    invoice_date: grnData.invoice_date,

    delivery_challan_number: grnData.delivery_challan_number?.trim(),

    delivery_date: grnData.delivery_date,

    grn_date: grnData.grn_date || todayGrnDate(),

    received_by: actorId || undefined,

    remarks: grnData.remarks?.trim(),

    payment_type: grnData.payment_type || 'CREDIT',

    gate_entry_number: grnData.gate_entry_number?.trim(),

    gate_entry_date: grnData.gate_entry_date,

    place_of_supply: grnData.place_of_supply?.trim(),

    external_po_reference: grnData.external_po_reference?.trim() || null,

  });



  return withStoreCreateBody({

    ...header,

    items,

  });

}



export function normalizeGrnStatus(status) {

  const s = String(status || '').toUpperCase();

  if (s === 'QC_COMPLETE') return 'QC_COMPLETED';

  return s;

}



export function canPostGrnToStock(status) {

  return normalizeGrnStatus(status) === 'QC_COMPLETED';

}



export function isGrnPosted(status, postingStatus) {

  const posted = normalizeGrnStatus(postingStatus || status);

  return posted === 'POSTED_TO_STOCK' || posted === 'POSTED';

}



/** Map a persisted GRN line item back into wizard state (resume flow). */

export function mapGrnItemToWizardLine(item, displayName, catalog = null) {

  const received = Math.floor(Number(item.received_quantity) || 0);

  // Server accept/reject is only meaningful once QC has been applied. Before that
  // (DRAFT/QC_PENDING) it comes back as "0.000", which must NOT be treated as a real
  // rejection — default accepted to received so resume lands on a sensible QC baseline.
  const qcApplied = Boolean(item.qc_status) && item.qc_status !== 'PENDING';

  const accepted = qcApplied && item.accepted_quantity != null

    ? Math.floor(Number(item.accepted_quantity) || 0)

    : received;

  const rejected = qcApplied && item.rejected_quantity != null

    ? Math.floor(Number(item.rejected_quantity) || 0)

    : Math.max(0, received - accepted);

  const line = {

    grn_item_id: item.grn_item_id || item.id,

    item_id: item.item_id,

    item_name: displayName,

    uom_id: item.uom_id,

    uom_name: item.uom_name || item.base_uom_name || '',

    uom_code: item.uom_code || item.base_uom_code || '',

    po_item_id: item.purchase_order_item_id || item.po_item_id,

    purchase_order_item_id: item.purchase_order_item_id || item.po_item_id,

    ordered_quantity: item.ordered_quantity,

    pending_quantity: item.pending_quantity,

    received_quantity: String(received),

    accept_quantity: accepted,

    reject_quantity: rejected,

    rejection_reason: item.rejection_reason || '',

    batch_number: item.batch_number || '',

    expiry_date: item.expiry_date || '',

    manufacturer_batch_number: item.manufacturer_batch_number || '',

    manufacturing_date: item.manufacturing_date || '',

    unit_rate: item.purchase_rate ?? item.unit_rate ?? '',

    po_unit_rate: item.po_unit_rate != null ? String(item.po_unit_rate) : '',

    mrp: item.mrp != null ? String(item.mrp) : '',

    sale_rate: item.sale_rate != null ? String(item.sale_rate) : '',

    tax_percent: item.tax_percent ?? 0,

    discount_percent: item.discount_percent ?? 0,

    free_quantity: item.free_quantity != null ? String(item.free_quantity) : '0',

    rate_override_reason: item.rate_override_reason || '',

    is_batch_tracked: Boolean(item.is_batch_tracked),

    is_expiry_tracked: Boolean(item.is_expiry_tracked),

    item_code: item.item_code || '',

  };

  return catalog ? enrichLineWithItemTracking(line, catalog) : line;

}



export function extractGrnScope(grn = {}) {

  const scope = {};

  if (grn.tenant_id) scope.tenant_id = grn.tenant_id;

  if (grn.hospital_id) scope.hospital_id = grn.hospital_id;

  return scope;

}



/** Resolve tenant/hospital for direct GRN create from grnData, store, or vendor. */
export function resolveDirectGrnScope({ grnData = {}, stores = [], vendors = [] } = {}) {
  const scope = extractGrnScope(grnData);
  const store = stores.find(
    s => String(s.store_id || s.id) === String(grnData.receiving_store_id),
  );
  const vendor = vendors.find(
    v => String(v.vendor_id || v.id) === String(grnData.vendor_id),
  );
  if (store?.tenant_id) scope.tenant_id = store.tenant_id;
  if (store?.hospital_id) scope.hospital_id = store.hospital_id;
  if (!scope.tenant_id && vendor?.tenant_id) scope.tenant_id = vendor.tenant_id;
  if (!scope.hospital_id && vendor?.hospital_id) scope.hospital_id = vendor.hospital_id;
  return scope;
}



function initialReceivedQty(qty) {

  const n = Math.floor(Number(qty) || 0);

  return n > 0 ? String(n) : '';

}



/** Map a PO line item into wizard state (receipt + inline QC). */

export function mapPoItemToWizardLine(item, displayName, catalog = null) {

  const pendingQty = item.pending_quantity != null ? item.pending_quantity : item.ordered_quantity;

  const received = initialReceivedQty(pendingQty);

  const receivedInt = Math.floor(Number(received) || 0);

  const poRate = item.unit_rate || item.purchase_rate || '';

  const line = {

    item_id: item.item_id,

    item_name: displayName,

    uom_id: item.uom_id,

    uom_name: item.uom_name || item.base_uom_name || '',

    uom_code: item.uom_code || item.base_uom_code || '',

    po_item_id: item.purchase_order_item_id || item.po_item_id || item.id,

    purchase_order_item_id: item.purchase_order_item_id || item.po_item_id || item.id,

    ordered_quantity: item.ordered_quantity,

    pending_quantity: pendingQty,

    received_quantity: received,

    accept_quantity: receivedInt,

    reject_quantity: 0,

    rejection_reason: '',

    batch_number: '',

    expiry_date: '',

    manufacturer_batch_number: item.manufacturer_batch_number || '',

    manufacturing_date: item.manufacturing_date || '',

    unit_rate: poRate,

    po_unit_rate: poRate !== '' && poRate != null ? String(poRate) : '',

    mrp: item.mrp != null ? String(item.mrp) : '',

    sale_rate: item.sale_rate != null ? String(item.sale_rate) : '',

    tax_percent: item.tax_percent ?? 0,

    po_tax_percent: item.tax_percent != null ? String(item.tax_percent) : '',

    discount_percent: item.discount_percent ?? 0,

    po_discount_percent: item.discount_percent != null ? String(item.discount_percent) : '',

    rate_override_reason: '',

    free_quantity: item.free_quantity != null ? String(item.free_quantity) : '0',

    is_batch_tracked: Boolean(item.is_batch_tracked),

    is_expiry_tracked: Boolean(item.is_expiry_tracked),

    item_code: item.item_code || '',

  };

  return catalog ? enrichLineWithItemTracking(line, catalog) : line;

}



/** Map a backend GRN validation message to a line index and field for inline UI errors. */

export function parseGrnApiLineError(message, lines = [], catalog = null) {

  if (!message || typeof message !== 'string') return null;

  const codeMatch = message.match(/Item '([^']+)'/);

  if (!codeMatch) return null;

  const itemCode = codeMatch[1];

  const field = /expiry/i.test(message) ? 'expiry_date' : 'batch_number';

  const lineIndex = lines.findIndex((line) => {

    const master = catalog?.byId?.get(line.item_id);

    const code = master?.item_code || line.item_code;

    return code === itemCode;

  });

  if (lineIndex < 0) return null;

  return { lineIndex, field, message };

}



export function syncLineReceivedQty(line, receivedQty) {

  const received = Math.floor(Number(receivedQty) || 0);

  const acc = Math.min(

    received,

    Math.floor(Number(line.accept_quantity ?? received) || 0),

  );

  return {

    ...line,

    received_quantity: receivedQty,

    accept_quantity: acc,

    reject_quantity: received - acc,

    discount_amount: formatDecimal(
      computeLineDiscountAmount(received, line.unit_rate, line.discount_percent),
    ),

  };

}



export function syncLineAcceptQty(line, acceptQty) {

  const received = Math.floor(Number(line.received_quantity) || 0);

  const acc = Math.min(received, Math.floor(Number(acceptQty) || 0));

  return {

    ...line,

    accept_quantity: acc,

    reject_quantity: received - acc,

  };

}



/** Free units are a second QC pool: capped by free_quantity, never by received. */

export function syncLineAcceptFreeQty(line, acceptFreeQty) {

  const free = Math.floor(Number(line.free_quantity) || 0);

  const acc = Math.min(free, Math.floor(Number(acceptFreeQty) || 0));

  return {

    ...line,

    accept_free_quantity: acc,

    reject_free_quantity: free - acc,

  };

}



/**
 * Free units the line actually accepts, mirroring the backend default: the
 * scheme rides with the purchase, so untouched free qty follows the billed
 * outcome, and a fully-rejected lot keeps its unpaid bonus out of stock.
 */

export function resolveLineAcceptFreeQty(line) {

  const free = Math.floor(Number(line.free_quantity) || 0);

  if (free <= 0) return 0;

  const accepted = Math.floor(Number(line.accept_quantity ?? line.received_quantity) || 0);

  if (accepted <= 0) return 0;

  const raw = line.accept_free_quantity;

  if (raw == null || raw === '') return free;

  return Math.min(free, Math.max(0, Math.floor(Number(raw) || 0)));

}



/** First QC validation failure message, or null. */

export function validateWizardLineQc(line) {

  const label = meaningfulItemName(line.item_name, line.item_id) || 'Line item';

  const received = Math.floor(Number(line.received_quantity) || 0);

  const acc = Math.floor(Number(line.accept_quantity ?? received) || 0);

  const rej = Math.floor(Number(line.reject_quantity ?? (received - acc)) || 0);

  if (acc < 0 || rej < 0) {

    return `${label}: Accepted and rejected quantities cannot be negative`;

  }

  if (acc > received) {

    return `${label}: Accept quantity cannot exceed received quantity`;

  }

  if (acc + rej > received) {

    return `${label}: Accepted + rejected cannot exceed received quantity`;

  }

  if (received - acc > 0 && !line.rejection_reason?.trim()) {

    return `${label}: Enter a rejection reason when quantity is rejected`;

  }

  const free = Math.floor(Number(line.free_quantity) || 0);

  const rawFree = line.accept_free_quantity;

  const enteredFree = rawFree == null || rawFree === '' ? null : Math.floor(Number(rawFree) || 0);

  if (enteredFree != null && enteredFree < 0) {

    return `${label}: Accepted free quantity cannot be negative`;

  }

  if (enteredFree != null && enteredFree > free) {

    return `${label}: Accepted free quantity cannot exceed free quantity of ${free}`;

  }

  // A fully-rejected lot drops its free units too, so there is nothing left to
  // accept and no expiry exposure from them — hence resolve, not the raw entry.

  const accFree = resolveLineAcceptFreeQty(line);

  if ((acc > 0 || accFree > 0) && line.expiry_date && line.expiry_date <= todayGrnDate() && !line.allow_expired_override) {

    return `${label}: Cannot accept expired stock without enabling the expired-override`;

  }

  return null;

}



export function toGrnItemAddBody(line) {

  return {

    item_id: line.item_id,

    uom_id: line.uom_id,

    po_item_id: line.po_item_id,

    ordered_quantity: Math.floor(Number(line.ordered_quantity) || 0),

    received_quantity: parseInt(line.received_quantity, 10),

    batch_number: line.batch_number || undefined,

    expiry_date: line.expiry_date || undefined,

    unit_rate: Number(line.unit_rate),

  };

}



/** Derive the backend qc_status for a wizard line from its accept/received quantities. */
export function deriveQcStatusFromLine(line) {

  const received = Math.floor(Number(line.received_quantity) || 0);

  const acc = Math.min(received, Math.floor(Number(line.accept_quantity ?? received) || 0));

  if (received > 0 && acc <= 0) return 'FAILED';

  if (acc >= received) return 'PASSED';

  return 'PARTIALLY_PASSED';

}



/** Build the POST /inventory/qc/apply-qc/ body (minus grn_item_id/scope) for a wizard line. */
export function toLineQcPayload(line) {

  const received = Math.floor(Number(line.received_quantity) || 0);

  const acc = Math.min(received, Math.floor(Number(line.accept_quantity ?? received) || 0));

  const rejected = received - acc;

  const free = Math.floor(Number(line.free_quantity) || 0);

  const accFree = resolveLineAcceptFreeQty(line);

  const payload = {

    qc_status: deriveQcStatusFromLine(line),

    accepted_quantity: String(acc),

    rejected_quantity: String(rejected),

  };

  // Only sent when the vendor actually shipped scheme units. Omitting them lets
  // the backend apply its default (free follows the billed outcome), which is
  // the right behaviour for every line without a scheme.

  if (free > 0) {

    payload.accepted_free_quantity = String(accFree);

    payload.rejected_free_quantity = String(free - accFree);

  }

  if (rejected > 0) {

    payload.rejection_reason = (line.rejection_reason || '').trim() || 'Rejected during QC';

  }

  if (
    acc > 0
    && line.expiry_date
    && line.expiry_date <= todayGrnDate()
    && line.allow_expired_override
  ) {

    payload.allow_expired_override = true;

  }

  return payload;

}


